import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Seo } from "@/components/Seo";
import { PageHeader } from "@/components/common/PageHeader";
import { ArticleCard } from "@/components/cards/ArticleCard";
import { Search } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext } from "@/components/ui/pagination";

export default function Knowledge() {
  const [data, setData] = useState({ items: [], total: 0, pages: 1, page: 1 });
  const [meta, setMeta] = useState({ categories: [] });
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [category, setCategory] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/articles/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(() => { setDebounced(search); setPage(1); }, 400); return () => clearTimeout(t); }, [search]);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page), limit: "9" });
    if (debounced) params.set("search", debounced);
    if (category) params.set("category", category);
    api.get(`/articles?${params.toString()}`).then((r) => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [page, debounced, category]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      <Seo title="Knowledge Center" description="Clear, honest dental education articles from the CDT clinical team." path="/knowledge" />
      <PageHeader eyebrow="Knowledge Center" title="Learn before you decide" subtitle="Honest, educational articles to help you make confident decisions about your oral health." />

      <section className="py-12 lg:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center justify-between mb-8">
            <div className="relative w-full lg:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--cdt-muted)]" />
              <input data-testid="knowledge-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search articles..." className="w-full h-11 pl-10 pr-4 rounded-[var(--cdt-radius-sm)] bg-white border border-[color:var(--cdt-border)] text-sm focus:outline-none focus:ring-2 focus:ring-[color:var(--cdt-ring)]" />
            </div>
            <div className="flex flex-wrap gap-2" data-testid="knowledge-categories">
              <button onClick={() => { setCategory(""); setPage(1); }} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${category === "" ? "bg-[var(--cdt-primary)] text-white border-[var(--cdt-primary)]" : "bg-white border-[color:var(--cdt-border)] text-[var(--cdt-text)]"}`}>All</button>
              {meta.categories.map((c) => (
                <button key={c} onClick={() => { setCategory(c); setPage(1); }} data-testid={`knowledge-cat-${c}`} className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${category === c ? "bg-[var(--cdt-primary)] text-white border-[var(--cdt-primary)]" : "bg-white border-[color:var(--cdt-border)] text-[var(--cdt-text)] hover:bg-[var(--cdt-surface-2)]"}`}>{c}</button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">{Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-72 rounded-[var(--cdt-radius-md)]" />)}</div>
          ) : data.items.length === 0 ? (
            <div className="text-center py-20 text-[var(--cdt-muted)]">No articles found. Try a different search or category.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
              {data.items.map((a, i) => <ArticleCard key={a.slug} article={a} index={i} />)}
            </div>
          )}

          {data.pages > 1 && (
            <div className="mt-12" data-testid="knowledge-pagination">
              <Pagination>
                <PaginationContent>
                  <PaginationItem><PaginationPrevious href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.max(1, p - 1)); }} /></PaginationItem>
                  {Array.from({ length: data.pages }).map((_, i) => (
                    <PaginationItem key={i}><PaginationLink href="#" isActive={data.page === i + 1} onClick={(e) => { e.preventDefault(); setPage(i + 1); }}>{i + 1}</PaginationLink></PaginationItem>
                  ))}
                  <PaginationItem><PaginationNext href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.min(data.pages, p + 1)); }} /></PaginationItem>
                </PaginationContent>
              </Pagination>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
